import { ProfileEndpoint as ProfileEndpointInterface } from "@/projectRoot/api-client/interfaces/endpoints/Profile.endpoint";
import ApiResponseDto from "@apiClient/interfaces/response/ApiResponse.dto";
import { KyInstance } from "ky/distribution/types/ky";
import { KyResponse } from "ky";
import DeviceTokenGenerateResponseDto from "@/projectRoot/api-client/interfaces/response/auth/DeviceTokenGenerateResponse.dto";
import CustomerDto from "@/projectRoot/api-client/interfaces/models/customers/Customer.dto";
import UpdateRequestDto from "@/projectRoot/api-client/interfaces/request/auth/UpdateRequest.dto";
import ChangePasswordRequestDto from "@/projectRoot/api-client/interfaces/request/auth/ChangePasswordRequest.dto";
import UpdateAddressRequestDto from "@/projectRoot/api-client/interfaces/request/auth/UpdateAddressRequest.dto";
import LoyaltyTransactionDto from "@/projectRoot/api-client/interfaces/models/customers/loyalty/LoyaltyTransaction.dto";
import NewsletterDto from "@/projectRoot/api-client/interfaces/models/marketing/Newsletter.dto";
import OrderDto from "@/projectRoot/api-client/interfaces/models/webshop/order/Order.dto";
import WishlistDto from "@/projectRoot/api-client/interfaces/models/webshop/wishlist/WIshlist.dto";
import WishlistCreateRequestDto from "@/projectRoot/api-client/interfaces/request/webshop/WishlistCreateRequest.dto";

export class ProfileEndpoint implements ProfileEndpointInterface {

    private _httpClient: KyInstance;
    private cache: RequestCache;

    public constructor(client: KyInstance, cache: RequestCache = "no-store") {
        this._httpClient = client;
        this.cache = cache;
    }

    get httpClient(): KyInstance {
        if (!this._httpClient) {
            throw new Error('Ky http client is not initialized');
        }
        return this._httpClient;
    }

    set httpClient(kyInstance: KyInstance) {
        this._httpClient = kyInstance;
    }

    /**
     * /api/profile/identity
     */
    async identity(): Promise<ApiResponseDto<CustomerDto, undefined>> {
        return this._httpClient
            .get(`profile/identity`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<CustomerDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/profile/update
     */
    async update(request: UpdateRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`profile/update`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/profile/create-address
     */
    async createAddress(request: UpdateAddressRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`profile/create-address`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/profile/update-address
     */
    async updateAddress(request: UpdateAddressRequestDto,uid:string): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`profile/update-address/` + uid, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/profile/delete-address
     */
    async deleteAddress(uid:string): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`profile/delete-address/` + uid, { cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }
    
    /**
     * /api/profile/change-password
     */
    async changePassword(request: ChangePasswordRequestDto): Promise<ApiResponseDto<DeviceTokenGenerateResponseDto, undefined>> {
        return this._httpClient
            .post(`profile/change-password`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<DeviceTokenGenerateResponseDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /*    
    * /api/profile/loyalty/transactions
     */
    async getLoyaltyTransactions(): Promise<ApiResponseDto<LoyaltyTransactionDto[], undefined>> {
        return this._httpClient
            .get(`profile/loyalty/transactions`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<LoyaltyTransactionDto[], undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/profile/wishlist
     */
    async wishlist(): Promise<ApiResponseDto<WishlistDto[], undefined>> {
        return this._httpClient
            .get(`profile/wishlist`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<WishlistDto[], undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/profile/wishlist
     */
    async wishlistAdd(request: WishlistCreateRequestDto): Promise<ApiResponseDto<WishlistDto, undefined>> {
        return this._httpClient
            .post(`profile/wishlist`, { json: request, cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<WishlistDto, undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/profile/wishlist/{product_id}
     */
    async wishlistRemove(productId: string): Promise<ApiResponseDto<undefined, undefined>> {
        return this._httpClient
            .delete(`profile/wishlist/` + productId, { cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<undefined, undefined> = await kyResponse.json();
                return response;
            });
    }

    /*    
    * /api/profile/subscriptions
     */
    async subscriptions(): Promise<ApiResponseDto<NewsletterDto[], undefined>> {
        return this._httpClient
            .get(`profile/subscriptions`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<NewsletterDto[], undefined> = await kyResponse.json();
                return response;
            });
    }

    /*    
    * /api/profile/orders
     */
    async orders(): Promise<ApiResponseDto<OrderDto[], undefined>> {
        return this._httpClient
            .get(`profile/orders`, {cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<OrderDto[], undefined> = await kyResponse.json();
                return response;
            });
    }

    /**
     * /api/profile/orders/{order_id}
     */
    async singleOrder(order_id: string): Promise<ApiResponseDto<OrderDto, undefined>> {
        return this._httpClient
            .get(`profile/orders/${order_id}`, { cache: this.cache })
            .then(async (kyResponse: KyResponse) => {
                if (kyResponse.status < 200 || kyResponse.status > 299) {
                    throw new Error(kyResponse.statusText)
                }
                const response: ApiResponseDto<OrderDto, undefined> = await kyResponse.json();
                return response;
            });
    }
}

export default ProfileEndpoint;